import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Divider, 
  TextField, 
  Tabs, 
  Tab, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { 
  Save, 
  Preview, 
  Add, 
  Edit, 
  Delete, 
  DragIndicator, 
  TextFields, 
  CheckBox, 
  RadioButtonChecked, 
  List, 
  DateRange, 
  FormatListNumbered,
  Image,
  Title
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert';
import VistaPrevia from './VistaPrevia';
import VariablesEtapaSelector from './VariablesEtapaSelector';
import plantillaService from '../../services/plantillaService';
import membreteService from '../../services/membreteService';

const FIELD_TYPES = [
  { id: 'textfield', name: 'Campo de texto', icon: <TextFields /> },
  { id: 'textarea', name: 'Área de texto', icon: <TextFields /> },
  { id: 'checkbox', name: 'Casilla de verificación', icon: <CheckBox /> },
  { id: 'radio', name: 'Opción múltiple', icon: <RadioButtonChecked /> },
  { id: 'select', name: 'Lista desplegable', icon: <List /> },
  { id: 'date', name: 'Fecha', icon: <DateRange /> },
  { id: 'number', name: 'Número', icon: <FormatListNumbered /> },
  { id: 'heading', name: 'Encabezado', icon: <Title /> },
  { id: 'image', name: 'Imagen', icon: <Image /> }
];

const EditorPlantillaAvanzado = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plantilla, setPlantilla] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [content, setContent] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [currentField, setCurrentField] = useState(null);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ text: '', type: '' });
  const [variables, setVariables] = useState([]);
  const [membretes, setMembretes] = useState([]);
  const [selectedMembrete, setSelectedMembrete] = useState(null);
  const quillRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          const plantillaData = await plantillaService.getPlantillaById(id);
          setPlantilla(plantillaData);
          
          if (plantillaData.contenido) {
            setContent(plantillaData.contenido);
          }
          
          if (plantillaData.contenido_html) {
            setHtmlContent(plantillaData.contenido_html);
          }
          
          if (plantillaData.campos) {
            try {
              const parsedFields = JSON.parse(plantillaData.campos);
              setFields(Array.isArray(parsedFields) ? parsedFields : []);
            } catch (e) {
              console.error('Error al parsear campos:', e);
              setFields([]);
            }
          }
          
          if (plantillaData.variables) {
            try {
              const parsedVariables = JSON.parse(plantillaData.variables);
              setVariables(Array.isArray(parsedVariables) ? parsedVariables : []);
            } catch (e) {
              console.error('Error al parsear variables:', e);
              setVariables([]);
            }
          }
          
          if (plantillaData.membrete_id) {
            setSelectedMembrete(plantillaData.membrete_id);
          }
        }
        
        // Cargar membretes
        const membretesData = await membreteService.getMembretes();
        setMembretes(membretesData);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setAlertMessage({ text: 'Error al cargar los datos de la plantilla', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleContentChange = (value) => {
    setContent(value);
  };
  
  const handleHtmlContentChange = (e) => {
    setHtmlContent(e.target.value);
  };
  
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setFields(items);
  };
  
  const handleAddField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: `Nuevo campo ${type}`,
      name: `field_${Date.now()}`,
      required: false,
      placeholder: '',
      options: type === 'radio' || type === 'select' ? ['Opción 1', 'Opción 2'] : [],
      defaultValue: '',
      size: 'medium'
    };
    
    setCurrentField(newField);
    setFieldDialogOpen(true);
  };
  
  const handleEditField = (field) => {
    setCurrentField({ ...field });
    setFieldDialogOpen(true);
  };
  
  const handleDeleteField = (fieldId) => {
    setFields(fields.filter(field => field.id !== fieldId));
  };
  
  const handleFieldDialogClose = () => {
    setFieldDialogOpen(false);
    setCurrentField(null);
  };
  
  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentField({
      ...currentField,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleOptionsChange = (index, value) => {
    const newOptions = [...currentField.options];
    newOptions[index] = value;
    setCurrentField({
      ...currentField,
      options: newOptions
    });
  };
  
  const handleAddOption = () => {
    setCurrentField({
      ...currentField,
      options: [...currentField.options, `Opción ${currentField.options.length + 1}`]
    });
  };
  
  const handleRemoveOption = (index) => {
    const newOptions = [...currentField.options];
    newOptions.splice(index, 1);
    setCurrentField({
      ...currentField,
      options: newOptions
    });
  };
  
  const handleSaveField = () => {
    if (fields.some(f => f.id === currentField.id)) {
      // Actualizar campo existente
      setFields(fields.map(f => f.id === currentField.id ? currentField : f));
    } else {
      // Agregar nuevo campo
      setFields([...fields, currentField]);
    }
    setFieldDialogOpen(false);
    setCurrentField(null);
  };
  
  const handlePreviewToggle = () => {
    setPreviewOpen(!previewOpen);
  };
  
  const handleSave = async () => {
    if (!plantilla) return;
    
    setSaving(true);
    try {
      const updatedPlantilla = {
        ...plantilla,
        contenido: content,
        contenido_html: htmlContent,
        campos: JSON.stringify(fields),
        variables: JSON.stringify(variables),
        membrete_id: selectedMembrete
      };
      
      await plantillaService.updatePlantilla(plantilla.id, updatedPlantilla);
      setAlertMessage({ text: 'Plantilla guardada correctamente', type: 'success' });
    } catch (error) {
      console.error('Error al guardar la plantilla:', error);
      setAlertMessage({ text: 'Error al guardar la plantilla', type: 'error' });
    } finally {
      setSaving(false);
    }
  };
  
  const handleVariablesChange = (newVariables) => {
    setVariables(newVariables);
  };
  
  const handleMembreteChange = (e) => {
    setSelectedMembrete(e.target.value);
  };
  
  const renderFieldForm = () => {
    if (!currentField) return null;
    
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            name="label"
            label="Etiqueta"
            value={currentField.label}
            onChange={handleFieldChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="name"
            label="Nombre del campo"
            value={currentField.name}
            onChange={handleFieldChange}
            fullWidth
          />
        </Grid>
        {currentField.type !== 'heading' && currentField.type !== 'image' && (
          <Grid item xs={12}>
            <TextField
              name="placeholder"
              label="Texto de ayuda"
              value={currentField.placeholder}
              onChange={handleFieldChange}
              fullWidth
            />
          </Grid>
        )}
        {currentField.type === 'textfield' || currentField.type === 'textarea' || currentField.type === 'number' ? (
          <Grid item xs={12}>
            <TextField
              name="defaultValue"
              label="Valor por defecto"
              value={currentField.defaultValue}
              onChange={handleFieldChange}
              fullWidth
            />
          </Grid>
        ) : null}
        {(currentField.type === 'radio' || currentField.type === 'select') && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Opciones
            </Typography>
            {currentField.options.map((option, index) => (
              <Grid container spacing={1} key={index} alignItems="center">
                <Grid item xs={10}>
                  <TextField
                    value={option}
                    onChange={(e) => handleOptionsChange(index, e.target.value)}
                    fullWidth
                    size="small"
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveOption(index)}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              startIcon={<Add />}
              onClick={handleAddOption}
              size="small"
              sx={{ mt: 1 }}
            >
              Agregar opción
            </Button>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Tamaño</InputLabel>
            <Select
              name="size"
              value={currentField.size}
              onChange={handleFieldChange}
              label="Tamaño"
            >
              <MenuItem value="small">Pequeño</MenuItem>
              <MenuItem value="medium">Mediano</MenuItem>
              <MenuItem value="large">Grande</MenuItem>
              <MenuItem value="full">Completo</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6} display="flex" alignItems="center">
          <FormControl component="fieldset">
            <label>
              <input
                type="checkbox"
                name="required"
                checked={currentField.required}
                onChange={handleFieldChange}
              />
              Campo obligatorio
            </label>
          </FormControl>
        </Grid>
      </Grid>
    );
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!plantilla) {
    return (
      <Box p={3}>
        <Alert message="No se encontró la plantilla especificada" type="error" />
        <Button
          variant="contained"
          onClick={() => navigate('/configuracion/plantillas')}
          sx={{ mt: 2 }}
        >
          Volver
        </Button>
      </Box>
    );
  }
  
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Editor de Plantilla: {plantilla.nombre}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Preview />}
            onClick={handlePreviewToggle}
            sx={{ mr: 1 }}
          >
            Vista Previa
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            Guardar
          </Button>
        </Box>
      </Box>
      
      {alertMessage.text && (
        <Alert 
          message={alertMessage.text} 
          type={alertMessage.type} 
          onClose={() => setAlertMessage({ text: '', type: '' })} 
        />
      )}
      
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Editor Visual" />
          <Tab label="Editor HTML" />
          <Tab label="Campos de Formulario" />
          <Tab label="Variables" />
          <Tab label="Configuración" />
        </Tabs>
      </Paper>
      
      {tabValue === 0 && (
        <Paper sx={{ p: 2 }}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={content}
            onChange={handleContentChange}
            style={{ height: '500px', marginBottom: '50px' }}
          />
        </Paper>
      )}
      
      {tabValue === 1 && (
        <Paper sx={{ p: 2 }}>
          <TextField
            label="Contenido HTML"
            multiline
            rows={20}
            value={htmlContent}
            onChange={handleHtmlContentChange}
            fullWidth
            variant="outlined"
          />
        </Paper>
      )}
      
      {tabValue === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Tipos de Campos
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={1}>
                {FIELD_TYPES.map((type) => (
                  <Grid item xs={6} key={type.id}>
                    <Button
                      variant="outlined"
                      startIcon={type.icon}
                      onClick={() => handleAddField(type.id)}
                      fullWidth
                      sx={{ mb: 1, justifyContent: 'flex-start' }}
                    >
                      {type.name}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 2, minHeight: '500px' }}>
              <Typography variant="h6" gutterBottom>
                Estructura del Formulario
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="fields">
                  {(provided) => (
                    <Box
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ minHeight: '200px' }}
                    >
                      {fields.length === 0 ? (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="200px"
                          border="1px dashed grey"
                          borderRadius={1}
                        >
                          <Typography color="textSecondary">
                            Arrastre campos aquí o haga clic en un tipo de campo para agregar
                          </Typography>
                        </Box>
                      ) : (
                        fields.map((field, index) => (
                          <Draggable key={field.id} draggableId={field.id} index={index}>
                            {(provided) => (
                              <Paper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                elevation={1}
                                sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center' }}
                              >
                                <Box {...provided.dragHandleProps} sx={{ mr: 1 }}>
                                  <DragIndicator />
                                </Box>
                                <Box flexGrow={1}>
                                  <Typography variant="subtitle1">
                                    {field.label}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    Tipo: {FIELD_TYPES.find(t => t.id === field.type)?.name || field.type}
                                    {field.required && ' (Obligatorio)'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Tooltip title="Editar campo">
                                    <IconButton
                                      color="primary"
                                      onClick={() => handleEditField(field)}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Eliminar campo">
                                    <IconButton
                                      color="error"
                                      onClick={() => handleDeleteField(field.id)}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Paper>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {tabValue === 3 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Variables disponibles
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <VariablesEtapaSelector
            variables={variables}
            onChange={handleVariablesChange}
            plantilla={plantilla}
          />
        </Paper>
      )}
      
      {tabValue === 4 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Configuración de la plantilla
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Membrete</InputLabel>
                <Select
                  value={selectedMembrete || ''}
                  onChange={handleMembreteChange}
                  label="Membrete"
                >
                  <MenuItem value="">
                    <em>Sin membrete</em>
                  </MenuItem>
                  {membretes.map((membrete) => (
                    <MenuItem key={membrete.id} value={membrete.id}>
                      {membrete.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {/* Diálogo para editar propiedades del campo */}
      <Dialog open={fieldDialogOpen} onClose={handleFieldDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentField?.id ? 'Editar Campo' : 'Nuevo Campo'}
        </DialogTitle>
        <DialogContent>
          {renderFieldForm()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFieldDialogClose} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSaveField} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo de vista previa */}
      <Dialog open={previewOpen} onClose={handlePreviewToggle} maxWidth="lg" fullWidth>
        <DialogTitle>Vista Previa de la Plantilla</DialogTitle>
        <DialogContent>
          <VistaPrevia
            contenido={content}
            contenido_html={htmlContent}
            campos={fields}
            membrete_id={selectedMembrete}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewToggle} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditorPlantillaAvanzado;